import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { SOURCE_TYPES, LEGACY_SOURCE } from '@/lib/sourceChannelTypes';

export interface SourceGoal {
  id?: string;
  user_id: string;
  year: number;
  month: number;
  business_unit: string | null;
  source: string;
  weight_pct: number; // integer: 35 = 35%
  goal_amount: number;
  goal_deals: number;
}

export interface SourceActuals {
  source: string;
  revenue: number;
  deal_count: number;
}

export interface GlobalSourceWeight {
  id?: string;
  year: number;
  month: number;
  business_unit: string | null;
  source: string;
  weight_pct: number;
}

// Normalize deals_source: null, empty, or 'Unselected' → 'Other'
function normalizeSource(source: string | null | undefined): string {
  if (!source || source === LEGACY_SOURCE || source === '') return 'Other';
  return source;
}

/**
 * Fetch global source weights for a given month/BU
 */
export function useGlobalSourceWeights(
  year: number,
  month: number,
  businessUnit: string | null
) {
  return useQuery({
    queryKey: ['global-source-weights', year, month, businessUnit],
    queryFn: async () => {
      let query = (supabase as any)
        .from('sales_goals_source_weights')
        .select('*')
        .eq('year', year)
        .eq('month', month);

      if (businessUnit) {
        query = query.eq('business_unit', businessUnit);
      }
      // When businessUnit is null ("Todas Unidades"), fetch ALL BUs and aggregate

      const { data, error } = await query;
      if (error) throw error;

      const rows = (data ?? []).map((row: any) => ({
        id: row.id,
        year: row.year,
        month: row.month,
        business_unit: row.business_unit,
        source: row.source,
        weight_pct: Number(row.weight_pct) || 0,
      })) as GlobalSourceWeight[];

      // If fetching all BUs, aggregate by source (take max weight per source)
      if (!businessUnit) {
        const sourceMap = new Map<string, GlobalSourceWeight>();
        rows.forEach(r => {
          const existing = sourceMap.get(r.source);
          if (!existing || r.weight_pct > existing.weight_pct) {
            sourceMap.set(r.source, r);
          }
        });
        return Array.from(sourceMap.values());
      }

      return rows;
    },
  });
}

/**
 * Upsert global source weights
 */
export function useUpsertGlobalSourceWeights(
  year: number,
  month: number,
  businessUnit: string | null
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (weights: Array<{ source: string; weightPct: number }>) => {
      if (!weights.length) return;

      const rows = weights.map(w => ({
        year,
        month,
        business_unit: businessUnit || null,
        source: w.source,
        weight_pct: w.weightPct,
        updated_at: new Date().toISOString(),
      }));

      const { error } = await (supabase as any)
        .from('sales_goals_source_weights')
        .upsert(rows, { onConflict: 'year,month,business_unit,source' });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['global-source-weights'] });
    },
  });
}

/**
 * Fetch source goal breakdown for multiple users in a given month/BU
 */
export function useSourceGoals(
  userIds: string[],
  year: number,
  month: number,
  businessUnit: string | null
) {
  return useQuery({
    queryKey: ['source-goals', userIds, year, month, businessUnit],
    queryFn: async () => {
      if (!userIds.length) return new Map<string, SourceGoal[]>();

      let query = supabase
        .from('user_sales_goals_by_source')
        .select('*')
        .in('user_id', userIds)
        .eq('year', year)
        .eq('month', month);

      if (businessUnit) {
        query = query.eq('business_unit', businessUnit);
      } else {
        query = query.is('business_unit', null);
      }

      const { data, error } = await query;
      if (error) throw error;

      const map = new Map<string, SourceGoal[]>();
      (data ?? []).forEach((row: any) => {
        const existing = map.get(row.user_id) || [];
        existing.push({
          id: row.id,
          user_id: row.user_id,
          year: row.year,
          month: row.month,
          business_unit: row.business_unit,
          source: row.source,
          weight_pct: Number(row.weight_pct) || 0,
          goal_amount: Number(row.goal_amount) || 0,
          goal_deals: Number(row.goal_deals) || 0,
        });
        map.set(row.user_id, existing);
      });

      return map;
    },
    enabled: userIds.length > 0,
  });
}

/**
 * Fetch actuals (deals WON) grouped by source for a set of consultants in a month.
 * Aligned with RPC get_sales_dashboard_metrics logic:
 * - revenue = total_amount - COALESCE(upsell_credit_amount, 0)
 * - cancelled deals excluded only if cancelled_at is in the same month as closed_date
 */
export function useSourceActuals(
  userIds: string[],
  year: number,
  month: number,
  businessUnit: string | null
) {
  return useQuery({
    queryKey: ['source-actuals', userIds, year, month, businessUnit],
    queryFn: async () => {
      if (!userIds.length) return new Map<string, SourceActuals[]>();

      // Build date range for the month
      const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const endMonth = month === 12 ? 1 : month + 1;
      const endYear = month === 12 ? year + 1 : year;
      const endDate = `${endYear}-${String(endMonth).padStart(2, '0')}-01`;

      const buFilter = businessUnit ? businessUnit : null;

      // Fetch won deals - we'll filter cancelled in JS to match RPC logic
      let query = supabase
        .from('deals')
        .select('deals_source, sales_consultant, total_amount, upsell_credit_amount, deal_status, cancelled_at, closed_date')
        .eq('stage', 'won')
        .gte('closed_date', startDate)
        .lt('closed_date', endDate)
        .in('sales_consultant', userIds);

      if (buFilter) {
        query = query.eq('business_unit', buFilter);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Group by consultant + normalized source
      // Exclude cancelled only if cancelled_at is in the same month as closed_date
      const map = new Map<string, SourceActuals[]>();
      const tempMap = new Map<string, Map<string, { revenue: number; count: number }>>();

      (data ?? []).forEach((deal: any) => {
        // Exclude cancelled deals only if cancelled in the same month
        if (deal.deal_status === 'cancelled' && deal.cancelled_at && deal.closed_date) {
          const closedMonth = deal.closed_date.substring(0, 7); // YYYY-MM
          const cancelledMonth = deal.cancelled_at.substring(0, 7);
          if (closedMonth === cancelledMonth) return; // skip
        }

        const userId = deal.sales_consultant;
        const source = normalizeSource(deal.deals_source);
        const revenue = (Number(deal.total_amount) || 0) - (Number(deal.upsell_credit_amount) || 0);

        if (!tempMap.has(userId)) tempMap.set(userId, new Map());
        const userMap = tempMap.get(userId)!;
        const existing = userMap.get(source) || { revenue: 0, count: 0 };
        existing.revenue += revenue;
        existing.count += 1;
        userMap.set(source, existing);
      });

      tempMap.forEach((sourceMap, userId) => {
        const actuals: SourceActuals[] = [];
        sourceMap.forEach((val, source) => {
          actuals.push({ source, revenue: val.revenue, deal_count: val.count });
        });
        map.set(userId, actuals);
      });

      return map;
    },
    enabled: userIds.length > 0,
  });
}

/**
 * Mutation to save source goals for a single user
 */
export function useUpsertSourceGoals(
  year: number,
  month: number,
  businessUnit: string | null
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (goals: Array<{ userId: string; source: string; weightPct: number; goalAmount: number; goalDeals: number }>) => {
      if (!goals.length) return;

      const rows = goals.map(g => ({
        user_id: g.userId,
        year,
        month,
        business_unit: businessUnit || null,
        source: g.source,
        weight_pct: g.weightPct,
        goal_amount: g.goalAmount,
        goal_deals: g.goalDeals,
        updated_at: new Date().toISOString(),
      }));

      const { error } = await supabase
        .from('user_sales_goals_by_source')
        .upsert(rows, { onConflict: 'user_id,year,month,business_unit,source' });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['source-goals'] });
    },
  });
}

/**
 * Get available source types for the dropdown
 */
export function getAvailableSources(): string[] {
  return SOURCE_TYPES.map(s => s.value);
}
